import { useState } from "react";
import { ShoppingCart } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Auto-opening clickwrap disclaimer shown when a user enters the shopping surface.
// Records the agreement on the account; "don't show again" permanently hides it.
export const ShoppingDisclaimerModal = ({ open, onClose }) => {
  const { refresh } = useAuth();
  const [dontShow, setDontShow] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const agree = async () => {
    setBusy(true);
    try {
      await api.post("/legal/shopping-disclaimer", { dont_show_again: dontShow });
      if (dontShow) await refresh();
    } catch {
      /* still let them through — the UI shouldn't trap them */
    } finally {
      setBusy(false);
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      data-testid="shopping-disclaimer-modal"
    >
      <div className="bg-white rounded-3xl w-full max-w-md p-7 shadow-2xl sms-fade-up">
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-2xl bg-[#eef3ea] flex items-center justify-center">
            <ShoppingCart size={30} className="text-[#4f7746]" />
          </div>
        </div>
        <h2 className="text-xl font-extrabold text-center text-[#171426] mb-2">Before you shop</h2>
        <p className="text-[#6b6880] text-center text-sm leading-relaxed">
          These products are <span className="font-semibold text-[#4a475c]">suggestions only</span>. Please do your own
          research — an item may not be exactly what your project needs. The final purchase decision is your
          responsibility, and Vittyyo is <span className="font-semibold text-[#4a475c]">not liable</span> for any
          incorrect, unsuitable, or unsatisfactory purchases.
        </p>

        <label
          className="flex items-center gap-2.5 justify-center mt-5 cursor-pointer select-none"
          data-testid="dont-show-again-label"
        >
          <input
            type="checkbox"
            data-testid="dont-show-again-checkbox"
            checked={dontShow}
            onChange={(e) => setDontShow(e.target.checked)}
            className="w-4 h-4 accent-[#4f7746] cursor-pointer"
          />
          <span className="text-sm font-medium text-[#6b6880]">Don't show this again</span>
        </label>

        <button
          data-testid="shopping-disclaimer-agree"
          onClick={agree}
          disabled={busy}
          className="sms-gradient-btn w-full text-white font-bold py-3.5 rounded-2xl mt-5 disabled:opacity-60"
        >
          {busy ? "One sec…" : "I Understand & Continue"}
        </button>
      </div>
    </div>
  );
};
